<template>
  <div class="profile-root">
    <!-- Profile Photo -->
    <div class="profile-photo">
      <img
        v-if="profile.profile_photo_url"
        :src="profile.profile_photo_url"
        :alt="$t('template.hatch_profile_photo_alt', 'Profile Photo')"
        class="profile-photo-img"
      />
      <div class="profile-id">{{ $t("template.hatch_profile_id", "ID") }}: {{ profile.id }}</div>
    </div>

    <!-- Main Info -->
    <div class="profile-main">
      <!-- Headline & Name -->
      <div class="profile-headline">
        <div class="profile-headline-text">{{ profile.ai_bio_headline }}</div>
        <span v-if="profile.match_type" class="profile-match-type">{{ profile.match_type }}</span>
      </div>
      <div class="profile-summary">{{ profile.ai_bio_summary }}</div>
      <div class="profile-extended">{{ profile.ai_bio_extended }}</div>

      <!-- Scores -->
      <div class="profile-scores">
        <div class="profile-score">
          <span class="profile-score-value">{{ profile.match_confidence }}%</span>
          <span class="profile-score-label">
            {{ $t("template.hatch_profile_match_confidence", "Match Confidence") }}
          </span>
        </div>
        <div class="profile-score">
          <span class="profile-score-value">{{ profile.info_accuracy }}%</span>
          <span class="profile-score-label">
            {{ $t("template.hatch_profile_info_accuracy", "Info Accuracy") }}
          </span>
        </div>
        <div class="profile-score">
          <span class="profile-score-value">{{ profile.social_influence }}</span>
          <span class="profile-score-label">
            {{ $t("template.hatch_profile_social_influence", "Social Influence") }}
          </span>
        </div>
        <div class="profile-score">
          <span class="profile-score-value">{{ profile.affluence_score }}</span>
          <span class="profile-score-label">
            {{ $t("template.hatch_profile_affluence", "Affluence") }}
          </span>
        </div>
      </div>

      <!-- Contact Info -->
      <div class="profile-contact">
        <div class="profile-contact-title">
          {{ $t("template.hatch_profile_contact", "Contact") }}
        </div>
        <div class="profile-contact-list">
          <div v-for="email in profile.emails" :key="email.id" class="profile-contact-item">
            <span class="material-icons profile-contact-icon">email</span>
            <span>{{ email.email }}</span>
            <span v-if="email.is_primary" class="profile-contact-primary">
              ({{ $t("template.hatch_profile_primary", "primary") }})
            </span>
          </div>
          <div v-for="phone in profile.phones" :key="phone.id" class="profile-contact-item">
            <span class="material-icons profile-contact-icon">phone</span>
            <span>{{ phone.phone }}</span>
            <span v-if="phone.is_primary" class="profile-contact-primary">
              ({{ $t("template.hatch_profile_primary", "primary") }})
            </span>
          </div>
        </div>
      </div>

      <!-- Address -->
      <div v-if="profile.addresses && profile.addresses.length" class="profile-address">
        <div class="profile-address-title">
          {{ $t("template.hatch_profile_address", "Address") }}
        </div>
        <div v-for="address in profile.addresses" :key="address.id" class="profile-address-item">
          <div>
            <span>{{ address.address1 }}</span>
            <span v-if="address.address2">, {{ address.address2 }}</span>
            <span>
              , {{ address.city }}, {{ address.state }} {{ address.postal_code }},
              {{ address.country }}
            </span>
            <span v-if="address.is_primary" class="profile-contact-primary">
              ({{ $t("template.hatch_profile_primary", "primary") }})
            </span>
          </div>
          <div v-if="address.zillow_url" class="profile-address-zillow">
            <a :href="fullZillowLink(address)" target="_blank" class="profile-address-zillow-link">
              {{ $t("template.hatch_profile_zillow", "Zillow") }}
            </a>
            <span v-if="address.zestimate">
              | {{ $t("template.hatch_profile_zestimate", "Zestimate") }}: ${{
                address.zestimate.toLocaleString()
              }}
            </span>
            <span v-if="address.year_built">
              | {{ $t("template.hatch_profile_built", "Built") }}: {{ address.year_built }}
            </span>
            <span v-if="address.bedrooms">
              | {{ $t("template.hatch_profile_beds", "Beds") }}: {{ address.bedrooms }}
            </span>
            <span v-if="address.bathrooms">
              | {{ $t("template.hatch_profile_baths", "Baths") }}: {{ address.bathrooms }}
            </span>
            <span v-if="address.square_feet">
              | {{ address.square_feet }} {{ $t("template.hatch_profile_sqft", "sqft") }}
            </span>
            <span v-if="address.rent_estimate">
              | {{ $t("template.hatch_profile_rent", "Rent") }}: ${{
                address.rent_estimate.toLocaleString()
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Education -->
      <div v-if="profile.education && profile.education.length" class="profile-education">
        <div class="profile-education-title">
          {{ $t("template.hatch_profile_education", "Education") }}
        </div>
        <div v-for="edu in profile.education" :key="edu.id" class="profile-education-item">
          <span class="profile-education-school">{{ edu.school }}</span>
          <span v-if="edu.degree">, {{ edu.degree }}</span>
          <span v-if="edu.start_date || edu.end_date" class="profile-education-dates">
            ({{ edu.start_date ? edu.start_date.substring(0, 4) : "" }} -
            {{ edu.end_date ? edu.end_date.substring(0, 4) : "" }})
          </span>
        </div>
      </div>

      <!-- Career -->
      <div v-if="profile.career && profile.career.length" class="profile-career">
        <div class="profile-career-title">{{ $t("template.hatch_profile_career", "Career") }}</div>
        <div v-for="job in profile.career" :key="job.id" class="profile-career-item">
          <span class="profile-career-title-text">{{ job.title }}</span>
          <span v-if="job.organization">
            {{ $t("template.hatch_profile_at", "at") }} {{ job.organization }}
          </span>
          <span v-if="job.is_current" class="profile-career-current">
            ({{ $t("template.hatch_profile_current", "current") }})
          </span>
          <span v-if="job.industry" class="profile-career-industry"> [{{ job.industry }}] </span>
          <span v-if="job.size" class="profile-career-size"> ({{ job.size }}) </span>
          <span v-if="job.website" class="profile-career-website">
            <a :href="job.website" target="_blank">
              {{ $t("template.hatch_profile_website", "Website") }}
            </a>
          </span>
        </div>
      </div>

      <!-- Social Links -->
      <div v-if="profile.social && profile.social.length" class="profile-social">
        <div class="profile-social-title">{{ $t("template.hatch_profile_social", "Social") }}</div>
        <div class="profile-social-list">
          <div v-for="soc in profile.social" :key="soc.id" class="profile-social-item">
            <span class="material-icons profile-social-icon">public</span>
            <a :href="soc.url" target="_blank" class="profile-social-link">{{ soc.network }}</a>
            <span v-if="soc.handle">({{ soc.handle }})</span>
            <span v-if="soc.followers" class="profile-social-followers">
              {{ soc.followers }} {{ $t("template.hatch_profile_followers", "followers") }}
            </span>
          </div>
        </div>
      </div>

      <!-- Scorecard (optional) -->
      <div v-if="profile.scorecard" class="profile-scorecard">
        <div class="profile-scorecard-title">
          {{ $t("template.hatch_profile_scorecard", "Scorecard") }}
        </div>
        <div class="profile-scorecard-grid">
          <div>
            {{ $t("template.hatch_profile_email_match", "Email Match") }}:
            <span
              :class="
                profile.scorecard.email_match ? 'profile-scorecard-yes' : 'profile-scorecard-no'
              "
            >
              {{
                profile.scorecard.email_match
                  ? $t("template.hatch_profile_yes", "Yes")
                  : $t("template.hatch_profile_no", "No")
              }}
            </span>
          </div>
          <div>
            {{ $t("template.hatch_profile_phone_match", "Phone Match") }}:
            <span
              :class="
                profile.scorecard.phone_match ? 'profile-scorecard-yes' : 'profile-scorecard-no'
              "
            >
              {{
                profile.scorecard.phone_match
                  ? $t("template.hatch_profile_yes", "Yes")
                  : $t("template.hatch_profile_no", "No")
              }}
            </span>
          </div>
          <div>
            {{ $t("template.hatch_profile_address_match", "Address Match") }}:
            <span
              :class="
                profile.scorecard.address_match ? 'profile-scorecard-yes' : 'profile-scorecard-no'
              "
            >
              {{
                profile.scorecard.address_match
                  ? $t("template.hatch_profile_yes", "Yes")
                  : $t("template.hatch_profile_no", "No")
              }}
            </span>
          </div>
          <div>
            {{ $t("template.hatch_profile_csz_match", "CSZ Match") }}:
            <span
              :class="
                profile.scorecard.csz_match ? 'profile-scorecard-yes' : 'profile-scorecard-no'
              "
            >
              {{
                profile.scorecard.csz_match
                  ? $t("template.hatch_profile_yes", "Yes")
                  : $t("template.hatch_profile_no", "No")
              }}
            </span>
          </div>
          <div>
            {{ $t("template.hatch_profile_state_only_match", "State Only Match") }}:
            <span
              :class="
                profile.scorecard.state_only_match
                  ? 'profile-scorecard-yes'
                  : 'profile-scorecard-no'
              "
            >
              {{
                profile.scorecard.state_only_match
                  ? $t("template.hatch_profile_yes", "Yes")
                  : $t("template.hatch_profile_no", "No")
              }}
            </span>
          </div>
          <div>
            {{ $t("template.hatch_profile_first_name_match", "First Name Match") }}:
            <span>{{ profile.scorecard.first_name_match }}</span>
          </div>
          <div>
            {{ $t("template.hatch_profile_last_name_match", "Last Name Match") }}:
            <span>{{ profile.scorecard.last_name_match }}</span>
          </div>
          <div>
            {{ $t("template.hatch_profile_middle_name_or_init", "Middle Name/Init") }}:
            <span>{{ profile.scorecard.middle_name_or_init_match }}</span>
          </div>
          <div>
            {{ $t("template.hatch_profile_total_score", "Total Score") }}:
            <span class="profile-scorecard-total">{{ profile.scorecard.total_final_score }}</span>
          </div>
          <div>
            {{ $t("template.hatch_profile_type", "Type") }}:
            <span>{{ profile.scorecard.match_type }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProfileView",

  props: {
    profile: {
      type: Object,
      required: true,
    },
  },

  methods: {
    fullZillowLink(address) {
      return `https://www.zillow.com${address.zillow_url}`;
    },
  },
};
</script>

<style scoped lang="scss">
/* Root container */
.profile-root {
  @apply tw-mx-auto tw-bg-white tw-rounded-xl tw-shadow-md tw-overflow-hidden tw-flex tw-flex-col md:tw-flex-row tw-p-6 tw-gap-6;
}

/* Profile Photo */
.profile-photo {
  @apply tw-flex-shrink-0 tw-flex tw-flex-col tw-items-center md:tw-items-start;
}
.profile-photo-img {
  @apply tw-w-32 tw-h-32 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-200;
}
.profile-id {
  @apply tw-mt-2 tw-text-xs tw-text-gray-400;
}

/* Main Info */
.profile-main {
  @apply tw-flex-1 tw-flex tw-flex-col tw-gap-2;
}
.profile-headline {
  @apply tw-flex tw-flex-col tw-gap-2;
}
.profile-headline-text {
  @apply tw-text-2xl tw-font-bold;
}
.profile-match-type {
  @apply tw-ml-2 tw-px-2 tw-py-1 tw-rounded tw-bg-green-100 tw-text-green-700 tw-text-xs tw-font-semibold;
}
.profile-summary {
  @apply tw-text-gray-600;
}
.profile-extended {
  @apply tw-text-gray-500 tw-text-sm;
}

/* Scores */
.profile-scores {
  @apply tw-flex tw-flex-wrap tw-gap-4 tw-mt-2;
}
.profile-score {
  @apply tw-flex tw-flex-col tw-items-center;
}
.profile-score-value {
  @apply tw-font-semibold;
}
.profile-score-label {
  @apply tw-text-xs tw-text-gray-400;
}

/* Contact Info */
.profile-contact {
  @apply tw-mt-4;
}
.profile-contact-title {
  @apply tw-font-semibold tw-text-gray-700 tw-mb-1;
}
.profile-contact-list {
  @apply tw-flex tw-flex-wrap tw-gap-4;
}
.profile-contact-item {
  @apply tw-flex tw-items-center tw-gap-1;
}
.profile-contact-icon {
  @apply tw-text-gray-400;
}
.profile-contact-primary {
  @apply tw-text-xs tw-text-blue-500 tw-ml-1;
}

/* Address */
.profile-address {
  @apply tw-mt-4;
}
.profile-address-title {
  @apply tw-font-semibold tw-text-gray-700 tw-mb-1;
}
.profile-address-item {
  @apply tw-mb-2;
}
.profile-address-zillow {
  @apply tw-text-xs tw-text-gray-400;
}
.profile-address-zillow-link {
  @apply tw-underline;
}

/* Education */
.profile-education {
  @apply tw-mt-4;
}
.profile-education-title {
  @apply tw-font-semibold tw-text-gray-700 tw-mb-1;
}
.profile-education-item {
  @apply tw-mb-1;
}
.profile-education-school {
  @apply tw-font-medium;
}
.profile-education-dates {
  @apply tw-text-xs tw-text-gray-400 tw-ml-2;
}

/* Career */
.profile-career {
  @apply tw-mt-4;
}
.profile-career-title {
  @apply tw-font-semibold tw-text-gray-700 tw-mb-1;
}
.profile-career-item {
  @apply tw-mb-1;
}
.profile-career-title-text {
  @apply tw-font-medium;
}
.profile-career-current {
  @apply tw-text-xs tw-text-green-500 tw-ml-1;
}
.profile-career-industry {
  @apply tw-text-xs tw-text-gray-400 tw-ml-2;
}
.profile-career-size {
  @apply tw-text-xs tw-text-gray-400 tw-ml-2;
}
.profile-career-website {
  @apply tw-text-xs tw-text-blue-500 tw-ml-2;
}

/* Social */
.profile-social {
  @apply tw-mt-4;
}
.profile-social-title {
  @apply tw-font-semibold tw-text-gray-700 tw-mb-1;
}
.profile-social-list {
  @apply tw-flex tw-flex-wrap tw-gap-4;
}
.profile-social-item {
  @apply tw-flex tw-items-center tw-gap-1;
}
.profile-social-icon {
  @apply tw-text-gray-400;
}
.profile-social-link {
  @apply tw-underline;
}
.profile-social-followers {
  @apply tw-text-xs tw-text-gray-400 tw-ml-1;
}

/* Scorecard */
.profile-scorecard {
  @apply tw-mt-4;
}
.profile-scorecard-title {
  @apply tw-font-semibold tw-text-gray-700 tw-mb-1;
}
.profile-scorecard-grid {
  @apply tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2 tw-text-xs tw-text-gray-600;
}
.profile-scorecard-yes {
  @apply tw-text-green-600;
}
.profile-scorecard-no {
  @apply tw-text-red-500;
}
.profile-scorecard-total {
  @apply tw-font-semibold;
}
</style>
